import { Box, Container } from "@material-ui/core"
import {
  postGiftCardCalculatePrice,
  postGiftCardGenerateVerificationCode,
} from "api"
import BackButton from "components/BackButton/BackButton"
import DiscountPromo from "components/DiscountPromo/DiscountPromo"
import GenericButton from "components/GenericButton/GenericButton"
import { GenericInput } from "components/GenericInput/GenericInput"
import { useOtpContext } from "components/OtpStep/OtpContext"
import PageTitle from "components/PageTitle/PageTitle"
import { useGroupPurchasesContext } from "components/PurchasePage/GroupPurchasesContext"
import LegalCheckboxes from "components/PurchasePage/LegalCheckboxes"
import { usePurchaseContext } from "components/PurchasePage/PurchaseContext"
import FormikForm from "components/Reusable/Bit/Form/FormikForm"
import { useRouter } from "next/router"
import { createRef, useEffect, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import styled from "styled-components"
import FormSectionTitle from "./Form/FormSectionTitle"
import validationSchema from "./Form/validationSchema"
import { useQuickPurchaseContext } from "./QuickPurchaseContext"

const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledWrapper = styled(Container)`
  padding: 108px 60px 80px 60px;
  display: flex;
  flex-direction: column;
  gap: 48px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    padding: 76px 16px 40px 16px;
    gap: 32px;
  }
`

const StyledTitleBox = styled("div")`
  display: flex;
  gap: 24px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    flex-direction: column;
    gap: 8px;
  }
`

const StyledSection = styled("div")`
  display: flex;
  flex-direction: column;

  ${(p) => p.theme.breakpoints.down("sm")} {
    margin-bottom: 30px;
  }

  ${(p) => p.theme.breakpoints.up("md")} {
    align-items: flex-start;
  }
`

const StyledRow = styled("div")`
  display: flex;
  justify-content: space-between;
  gap: 24px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    flex-direction: column;
    gap: 0;
  }
`

const StyledFormContainer = styled("div")`
  display: flex;
  flex-direction: column;

  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledSmallInputContainer = styled("div")`
  display: flex;
  gap: 24px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    gap: 16px;

    > * {
      width: 100%;
    }
  }
`

const StyledCheckboxContainer = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledSubmitButton = styled(GenericButton).attrs({
  variant: "contained",
})<{ isdisabled?: boolean }>`
  display: flex;
  width: 328px;
  height: 48px;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;

  background: var(--Yellow, #ffbc0d);
  color: #000;

  ${(p) => p.theme.breakpoints.down("sm")} {
    display: none;
  }

  ${(p) =>
    p.isdisabled &&
    `
    background: var(--Light-Grey, #f7f7f7);
    color: rgba(45, 45, 45, 0.72);
    font-size: 24px;
    :hover {
    background: var(--Light-Grey, #f7f7f7);

  }
  `}
`

const StyledForm = styled("div")``

let timeoutId

const debounce = (func, value) => {
  clearTimeout(timeoutId)
  timeoutId = setTimeout(async () => {
    func(value)
  }, 1000)
}

const QuickPurchaseProcess = ({ settings }) => {
  const [checkboxValues, setCheckboxValues] = useState(null)
  const { setPrevPath } = useGroupPurchasesContext()

  const reCaptchaRef = createRef<ReCAPTCHA>()

  const {
    setSelectedGiftCard,
    setGiftCardPrice: setPurchaseGiftCardPrice,
    setPurchaseData,
  } = usePurchaseContext()

  const {
    quickPurchaseInputValues,
    setPriceAfterDiscount,
    setshowStickyBottom,
    setValidationPassed,
    setSubmitFormTrigger,
    setQuickPurchaseInputValues,
    setShowExitModal,
    setDiscount,
    giftCardPrice,
    setGiftCardPrice,
  } = useQuickPurchaseContext()
  const { setTotalTimeoutInSeconds, setPhoneNumber } = useOtpContext()

  const router = useRouter()

  const getVerificationCode = async (
    phone: string,
    gRecaptchaResponse: string
  ) => {
    try {
      const response = await postGiftCardGenerateVerificationCode({
        mobilePhone: phone.replace(/-/g, ""),
        toSendSms: true,
        gRecaptchaResponse,
      })
      if (response?.status === 200 && response?.data) {
        setTotalTimeoutInSeconds(response.data.body.totalTimeoutInSeconds)
        router.push("/quick-purchase/otp")
      }
    } catch (error) {
      console.log(error)
      router.replace("/404")
    }
  }

  const handleSubmit = (values, recaptchaResponse) => {
    setQuickPurchaseInputValues(values)

    const purchaseDataObj = {
      quantity: 1,
      amount: values?.amount,
      phoneNumber: values?.RecipientMobilePhone,
      recipientName: values?.RecipientName,
      recipientEmail: values?.RecipientEmail,
      giftCardTypeId: 0,
      blessingId: 0,
      senderName: values?.SenderName,
      senderLastName: values?.SenderName,
      senderMobilePhone: values?.SenderMobilePhone,
      senderEmail: values?.SenderEmail,
      senderBlessing: "senderBlessing",
      approveMarketingContent: values?.checkedTwo,
      messagesScheduleDate: "" /* date-time */,
      allowanceStartDateTime: "" /* date-time */,
      allowanceEndDateTime: "" /* date-time */,
      giftCardScheduleType: "",
      audioBlessingBase64: "",
      audioBlessingContentType: "",
      audioBlessingExtension: "",
      audioDuration: 0,
      videoFileId: "",
      aiImageGuid: "",
    }

    // Set it here so that it can be used in PurchaseSummaryPage and rest of flow
    setPurchaseData(purchaseDataObj)

    // For the OTP form
    const phone = values?.SenderMobilePhone.replace(/-/g, "")
    setPhoneNumber(phone)
    getVerificationCode(phone, recaptchaResponse)
    router.push("/quick-purchase/otp")
  }

  let tmpFormik

  useEffect(() => {
    if (giftCardPrice > 0) {
      debounce(sendToServer, giftCardPrice)

      // Stops formik from resetting value on submit
      tmpFormik.setFieldValue("amount", giftCardPrice)
    }

    // Set so that the back button on Terms page works
    setPrevPath("/quick-purchase")
  }, [])

  const {
    amount,
    RecipientName,
    RecipientMobilePhone,
    RecipientEmail,
    SenderName,
    SenderLastName,
    SenderMobilePhone,
    SenderEmail,
  } = quickPurchaseInputValues

  const initialValues = {
    amount: amount || "",
    RecipientName: RecipientName || "",
    RecipientMobilePhone: RecipientMobilePhone || "",
    RecipientEmail: RecipientEmail || "",
    SenderName: SenderName || "",
    SenderLastName: SenderLastName || "",
    SenderMobilePhone: SenderMobilePhone || "",
    SenderEmail: SenderEmail || "",
    checkedOne: false,
    checkedTwo: false,
  }

  // Shows Stick Bottom Summary
  useEffect(() => {
    setshowStickyBottom(true)
    setSubmitFormTrigger(() => tmpFormik?.submitForm)

    setSelectedGiftCard({
      id: settings?.blessingId,
      categoryId: 0, // ?
      name: settings?.blessingSlug,
      order: 0,
      image: {
        imageUrl: "", // ?
        alt: "", // ?
      },
      blessingType: "", // ?
      blessingScheduleTypeDefault: "", // ?
      promotion: settings?.promotion,
      slug: settings?.blessingSlug,
      pageTitle: "", // ?
      metaTitle: "", // ?
      keywords: "", // ?
      metaDescription: "", // ?
      isAiImage: false,
    })

    return () => {
      setshowStickyBottom(false)
    }
  }, [])

  const saveGiftCardData = (values) => {
    // Will be used in future dev
    setQuickPurchaseInputValues(values)
    setGiftCardPrice(parseInt(values?.amount))
    setPurchaseGiftCardPrice(parseInt(values?.amount))
  }

  const sendToServer = async (value) => {
    const response = await postGiftCardCalculatePrice({
      quantity: 1,
      amount: value,
      blessingId: settings?.blessingId,
      allowanceStartDateTime: "",
      allowanceEndDateTime: "",
      giftCardScheduleType: "None",
    })

    setDiscount(response?.data?.body?.decreasePercentage)
    setPriceAfterDiscount(response?.data?.body?.price)
  }

  const setFormikValidation = (errors) => {
    if (Object.keys(errors).length === 0 && checkboxValues?.checkedOne) {
      setValidationPassed(true)
      return
    }
    setValidationPassed(false)
  }

  return (
    <>
      <StyledWrapper>
        <Box>
          <BackButton
            prevPath="/quick-purchase"
            linkText="חזרה"
            padding="12px 0 0 0"
            mobilePadding="18px 0 0 0"
            preventNavigation={true}
            beforeNavigation={() => setShowExitModal(true)}
          />
        </Box>

        <StyledTitleBox>
          <PageTitle titleText="גיפטקארד של מקדונלד'ס" />
          {settings?.promotion && (
            <DiscountPromo promoText={settings?.promotion?.description} />
          )}
        </StyledTitleBox>

        <StyledForm>
          <FormikForm
            validationSchema={validationSchema({
              min: settings?.minamuont,
              max: settings?.maxAmuont,
            })}
            initialValues={initialValues}
            onSubmit={async (values) => {
              reCaptchaRef?.current?.reset()
              const recaptchaResponse =
                await reCaptchaRef?.current?.executeAsync()
              handleSubmit(values, recaptchaResponse)
            }}
            render={(formik) => {
              tmpFormik = formik
              setFormikValidation(formik?.errors)
              return (
                <StyledFormContainer>
                  <StyledSection>
                    <FormSectionTitle
                      imgSrc="/mcdonalds-logo-confetti.svg"
                      imgAlt="McDonalds Confetti Logo"
                      titleText="כמה בא לך לתת?"
                    />
                    <GenericInput
                      name="amount"
                      label="הזנת סכום"
                      type="tel"
                      topMargin="24"
                      mobileTopMargin="8"
                      inputMode="numeric"
                      onChange={async (e) => {
                        formik.handleChange(e)
                        const value = e.target.value
                        if (value === "") {
                          formik.setFieldValue("amount", "")
                        } else {
                          formik.setFieldValue("amount", parseInt(value))
                        }

                        debounce(sendToServer, parseInt(value))
                      }}
                    />
                  </StyledSection>

                  <StyledSection>
                    <FormSectionTitle
                      imgSrc="/gift-box.svg"
                      imgAlt="McDonalds Gift Box"
                      titleText="למי המתנה?"
                    />
                    <StyledRow>
                      <GenericInput
                        name="RecipientName"
                        label="שם/שם חיבה (*חובה)"
                        $width="328"
                        topMargin="24"
                        mobileTopMargin="8"
                      />
                      <GenericInput
                        name="RecipientMobilePhone"
                        label="מספר נייד (*חובה)"
                        $width="328"
                        type="tel"
                        topMargin="24"
                        mobileTopMargin="8"
                      />
                      <GenericInput
                        name="RecipientEmail"
                        label="אימייל (רשות)"
                        $width="328"
                        type="email"
                        topMargin="24"
                        mobileTopMargin="8"
                      />
                    </StyledRow>
                  </StyledSection>

                  <StyledSection>
                    <FormSectionTitle
                      imgSrc="/mobile-phone.svg"
                      imgAlt="McDonalds Mobile Phone"
                      titleText="הפרטים שלי לתשלום"
                    />
                    <StyledRow>
                      <StyledSmallInputContainer>
                        <GenericInput
                          name="SenderName"
                          label="שם פרטי (*חובה)"
                          $width="156"
                          autoComplete="given-name"
                          topMargin="24"
                          mobileTopMargin="8"
                        />
                        <GenericInput
                          name="SenderLastName"
                          label="שם משפחה (*חובה)"
                          $width="156"
                          autoComplete="family-name"
                          topMargin="24"
                          mobileTopMargin="8"
                        />
                      </StyledSmallInputContainer>
                      <GenericInput
                        name="SenderMobilePhone"
                        label="מספר נייד (*חובה)"
                        $width="328"
                        type="tel"
                        autoComplete="tel-national"
                        topMargin="24"
                        mobileTopMargin="8"
                      />
                      <GenericInput
                        name="SenderEmail"
                        label="אימייל (*חובה)"
                        $width="328"
                        type="email"
                        autoComplete="email"
                        topMargin="24"
                        mobileTopMargin="8"
                      />
                    </StyledRow>
                  </StyledSection>

                  <StyledCheckboxContainer>
                    <LegalCheckboxes
                      saveGiftCardData={saveGiftCardData}
                      margin="0"
                      setValues={setCheckboxValues}
                    />
                    <StyledSubmitButton
                      btnType="filled"
                      type="submit"
                      isdisabled={
                        Object.keys(formik?.errors).length > 0 ||
                        !checkboxValues?.checkedOne
                      }
                    >
                      הכל נכון, הלאה
                    </StyledSubmitButton>
                  </StyledCheckboxContainer>
                  <ReCAPTCHA
                    ref={reCaptchaRef}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    tabIndex={-1}
                  />
                </StyledFormContainer>
              )
            }}
          />
        </StyledForm>
      </StyledWrapper>
    </>
  )
}

export default QuickPurchaseProcess
